import type { Unit } from '../enums'

/**
 * Every unit the application renders, in one table.
 *
 * `symbol` is the ONE spelling of a unit. Nothing else may write one out —
 * a second spelling is how a screen and a PDF come to disagree about a figure
 * they both read from the database.
 *
 * It sits in `shared/constants` rather than in the locale files because a
 * symbol is not German — `m²` is `m²` in every language. The long-form German
 * names live in `shared/locales/de/units.ts`, and only the pricing units have
 * one: a technical subsystem keeps its own vocabulary, so `W/(m·K)` renders as
 * itself and is never "Watt pro Meter-Kelvin".
 */

/**
 * The quantity a unit measures. Two units of one dimension convert into each
 * other; two of different dimensions do not, and neither does a unit that has
 * none.
 *
 * Absent for a unit nothing converts: a count, a lump sum, a rating, and the
 * scales that are not linear — °C and K sit apart by an offset, a decibel is
 * logarithmic, and a conversion that pretended otherwise would be silently
 * wrong.
 */
export type UnitDimension =
  | 'LENGTH'
  | 'AREA'
  | 'VOLUME'
  | 'MASS'
  | 'TIME'
  | 'PRESSURE'
  | 'POWER'

type UnitDefinition = {
  /** Rendered after a value, with a space. The only spelling of this unit. */
  symbol: string
  /**
   * What the unit converts by, where it converts at all.
   *
   * One object rather than two optional fields, so the pairing is the type's
   * rather than a comment's: a dimension without a factor cannot be written.
   * `factor` is how many of the dimension's base unit one of these is — metre,
   * square metre, cubic metre, kilogram, hour, pascal, watt.
   */
  scale?: { dimension: UnitDimension; factor: number }
}

export const UNITS = {
  // ─── Count ──────────────────────────────────────────────────────────
  PIECE: { symbol: 'Stk.' },

  // ─── Length ─────────────────────────────────────────────────────────
  MILLIMETER: { symbol: 'mm', scale: { dimension: 'LENGTH', factor: 0.001 } },
  CENTIMETER: { symbol: 'cm', scale: { dimension: 'LENGTH', factor: 0.01 } },
  METER: { symbol: 'm', scale: { dimension: 'LENGTH', factor: 1 } },
  KILOMETER: { symbol: 'km', scale: { dimension: 'LENGTH', factor: 1000 } },

  // ─── Area ───────────────────────────────────────────────────────────
  SQUARE_CENTIMETER: {
    symbol: 'cm²',
    scale: { dimension: 'AREA', factor: 0.0001 },
  },
  SQUARE_METER: { symbol: 'm²', scale: { dimension: 'AREA', factor: 1 } },

  // ─── Volume ─────────────────────────────────────────────────────────
  LITER: { symbol: 'l', scale: { dimension: 'VOLUME', factor: 0.001 } },
  CUBIC_METER: { symbol: 'm³', scale: { dimension: 'VOLUME', factor: 1 } },

  // ─── Weight ─────────────────────────────────────────────────────────
  GRAM: { symbol: 'g', scale: { dimension: 'MASS', factor: 0.001 } },
  KILOGRAM: { symbol: 'kg', scale: { dimension: 'MASS', factor: 1 } },
  TONNE: { symbol: 't', scale: { dimension: 'MASS', factor: 1000 } },

  // ─── Time ───────────────────────────────────────────────────────────
  HOUR: { symbol: 'Std.', scale: { dimension: 'TIME', factor: 1 } },
  DAY: { symbol: 'Tag', scale: { dimension: 'TIME', factor: 24 } },

  // ─── Price ──────────────────────────────────────────────────────────
  EURO: { symbol: '€' },
  /**
   * A flat price carries no unit at all. It is in this table because a measure
   * priced as a lump sum still has to say so where every other measure names a
   * unit, and an empty symbol renders as nothing next to the value.
   */
  FLAT: { symbol: '' },

  // ─── Thermal ────────────────────────────────────────────────────────
  WATT_PER_M_K: { symbol: 'W/(m·K)' },
  WATT_PER_SQM_K: { symbol: 'W/(m²·K)' },
  /** The parenthesised form is the standard one, and the one that wins. */
  KWH_PER_SQM_YEAR: { symbol: 'kWh/(m²·a)' },

  // ─── Energy ─────────────────────────────────────────────────────────
  KILOWATT: { symbol: 'kW', scale: { dimension: 'POWER', factor: 1000 } },
  KILOWATT_HOUR: { symbol: 'kWh' },
  KILOWATT_PEAK: { symbol: 'kWp' },

  // ─── Pressure ───────────────────────────────────────────────────────
  PASCAL: { symbol: 'Pa', scale: { dimension: 'PRESSURE', factor: 1 } },
  KILOPASCAL: { symbol: 'kPa', scale: { dimension: 'PRESSURE', factor: 1000 } },
  BAR: { symbol: 'bar', scale: { dimension: 'PRESSURE', factor: 100000 } },

  // ─── Sound ──────────────────────────────────────────────────────────
  DECIBEL: { symbol: 'dB' },
  DECIBEL_A: { symbol: 'dB(A)' },

  // ─── Temperature ────────────────────────────────────────────────────
  CELSIUS: { symbol: '°C' },
  KELVIN: { symbol: 'K' },

  // ─── Electrical ─────────────────────────────────────────────────────
  VOLT: { symbol: 'V' },
  AMPERE: { symbol: 'A' },
  WATT: { symbol: 'W', scale: { dimension: 'POWER', factor: 1 } },

  // ─── Force ──────────────────────────────────────────────────────────
  NEWTON: { symbol: 'N' },
  KILONEWTON: { symbol: 'kN' },

  // ─── Flow ───────────────────────────────────────────────────────────
  LITER_PER_SECOND: { symbol: 'l/s' },
  LITER_PER_SECOND_SQM: { symbol: 'l/(s·m²)' },

  // ─── Ratio ──────────────────────────────────────────────────────────
  PERCENT: { symbol: '%' },
  MM_PER_M: { symbol: 'mm/m' },
} as const satisfies Record<Unit, UnitDefinition>

/**
 * The units something can be priced and quantified in — a narrower set than the
 * column allows. Nothing is priced in decibels, so the picker and the schemas
 * the model answers against offer only these; the column takes any unit the
 * app renders.
 *
 * Order is the order the pricing picker offers them in.
 */
export const PRICING_UNIT_KEYS = [
  'PIECE',
  'SQUARE_METER',
  'CUBIC_METER',
  'METER',
  'CENTIMETER',
  'LITER',
  'KILOGRAM',
  'GRAM',
  'HOUR',
  'DAY',
  'FLAT',
] as const satisfies readonly Unit[]

export type PricingUnitKey = (typeof PRICING_UNIT_KEYS)[number]

/**
 * Whether a string coming from outside the app is a member — a scraped
 * datasheet, an imported catalogue. Inside, every unit column is the `Unit`
 * enum, so nothing has to ask.
 */
export const isUnitKey = (value: string): value is Unit => value in UNITS

/**
 * What a unit converts by, or null where it converts by nothing.
 *
 * Read through here rather than off `UNITS[unit]`: the table is `as const`, so
 * a member that converts by nothing has no such property to read at all and the
 * union does not offer one.
 */
export function unitScale(unit: Unit): UnitDefinition['scale'] | null {
  const { scale }: UnitDefinition = UNITS[unit]
  return scale ?? null
}

/**
 * The symbol to print after a value, or `''` where there is none — for a unit
 * that has no symbol (`FLAT`) as much as for a value that carries none.
 */
export function displayUnitSymbol(unit: Unit | null | undefined): string {
  return unit ? UNITS[unit].symbol : ''
}

/**
 * What a number input puts after the figure — the symbol with the space that
 * belongs in front of it, and nothing at all where there is none.
 *
 * Takes the symbol rather than the unit, because a field registry hands one
 * out ready-rendered. `undefined` rather than `''`, because that is what a
 * control reads as "no suffix"; three inputs deciding the space for
 * themselves is one of them away from setting `40m²`.
 */
export const unitSuffix = (symbol: string | null | undefined) =>
  symbol ? ` ${symbol}` : undefined
